import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Input } from '../../components/ui/input';
import { 
  TrendingUp, TrendingDown, DollarSign, Users, Calendar, 
  CreditCard, Download, Loader2, BarChart3, PieChart, Search,
  ChevronLeft, ChevronRight, Receipt
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminReports = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [reportPeriod, setReportPeriod] = useState('month');
  const [reportData, setReportData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Transactions state
  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [txSearchTerm, setTxSearchTerm] = useState('');
  const [txCurrentPage, setTxCurrentPage] = useState(1);
  const txItemsPerPage = 15;

  useEffect(() => {
    fetchReports();
  }, [reportPeriod]);

  useEffect(() => {
    if (activeTab === 'transactions') {
      fetchTransactions();
    }
  }, [activeTab]);

  const fetchTransactions = async () => {
    try {
      setTransactionsLoading(true);
      const practiceId = localStorage.getItem('practiceId');
      const response = await fetch(`${BACKEND_URL}/api/transactions?practiceId=${practiceId}`);
      if (response.ok) {
        const data = await response.json();
        setTransactions(data);
      }
    } catch (err) {
      console.error('Error fetching transactions:', err);
    } finally {
      setTransactionsLoading(false);
    }
  };

  const filteredTransactions = transactions.filter(tx => 
    (tx.clientName?.toLowerCase() || '').includes(txSearchTerm.toLowerCase()) ||
    (tx.clientEmail?.toLowerCase() || '').includes(txSearchTerm.toLowerCase()) ||
    (tx.items?.toLowerCase() || '').includes(txSearchTerm.toLowerCase())
  );

  const txTotalPages = Math.ceil(filteredTransactions.length / txItemsPerPage);
  const paginatedTransactions = filteredTransactions.slice(
    (txCurrentPage - 1) * txItemsPerPage,
    txCurrentPage * txItemsPerPage
  );

  const txTotalRevenue = transactions.reduce((sum, tx) => sum + (tx.amount || 0), 0);

  const exportTransactionsCSV = () => {
    const headers = ['Date', 'Client Name', 'Email', 'Phone', 'Items', 'Amount', 'Discount', 'Payment Method', 'Type'];
    const rows = transactions.map(tx => [
      tx.date ? new Date(tx.date).toLocaleDateString() : '',
      tx.clientName || '',
      tx.clientEmail || '',
      tx.phone || '',
      `"${tx.items || ''}"`,
      tx.amount || 0,
      tx.discount || 0,
      tx.paymentMethod || '',
      tx.type || ''
    ]);
    
    const csv = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transactions_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast({ title: 'Export Complete', description: 'Transactions exported to CSV' });
  };

  const exportToCSV = () => {
    if (!reportData) return;
    
    const headers = ['Category', 'Amount', 'Percentage'];
    const csvContent = [
      '# Shop Summary Report',
      `# Period: ${reportPeriod}`,
      `# Total Revenue: $${reportData.revenue.total.toLocaleString()}`,
      '',
      '## Revenue Breakdown',
      headers.join(','),
      ...reportData.revenue.breakdown.map(item => [
        item.category,
        item.amount,
        `${item.percentage}%`
      ].join(',')),
      '',
      '## Top Treatments',
      'Treatment,Revenue,Count,Change',
      ...reportData.topTreatments.map(t => [
        `"${t.name}"`,
        t.revenue,
        t.count,
        `${t.change}%`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `shop_summary_${reportPeriod}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    toast({ title: 'Export Complete', description: 'Shop summary exported to CSV' });
  };

  const fetchReports = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const practiceId = localStorage.getItem('practiceId') || 'demo-practice-001';
      
      const response = await fetch(
        `${BACKEND_URL}/api/reports?practiceId=${practiceId}&period=${reportPeriod}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        // Use API data if it has real values, otherwise use mock
        if (data.appointments?.total > 0 || data.patients?.total > 0) {
          setReportData(data);
        } else {
          setReportData(mockReportData);
        }
      } else {
        setReportData(mockReportData);
      }
    } catch (err) {
      console.error('Error fetching reports:', err);
      setReportData(mockReportData);
    } finally {
      setLoading(false);
    }
  };

  const mockReportData = {
    revenue: {
      total: 71250,
      change: 12.5,
      breakdown: [
        { category: 'Treatments', amount: 42500, percentage: 60 },
        { category: 'Memberships', amount: 21375, percentage: 30 },
        { category: 'Products', amount: 7125, percentage: 10 }
      ]
    },
    appointments: {
      total: 342,
      completed: 298,
      cancelled: 24,
      noShow: 20,
      change: 8.2
    },
    patients: {
      total: 156,
      newThisMonth: 23,
      returning: 133,
      memberConversion: 45
    },
    topTreatments: [
      { name: 'Botox', revenue: 15600, count: 130, change: 15 },
      { name: 'Hydrafacial', revenue: 11940, count: 60, change: 8 },
      { name: 'Dermal Fillers', revenue: 9750, count: 15, change: -3 },
      { name: 'Microneedling', revenue: 6000, count: 20, change: 22 },
      { name: 'Chemical Peel', revenue: 4375, count: 25, change: 5 }
    ],
    topProviders: [
      { name: 'Dr. Emily Chen', revenue: 32500, appointments: 145, rating: 4.9 },
      { name: 'Dr. Michael Torres', revenue: 28750, appointments: 132, rating: 4.8 },
      { name: 'Sarah Kim, RN', revenue: 10000, appointments: 65, rating: 4.7 }
    ],
    membershipStats: {
      totalMembers: 68,
      newSignups: 12,
      churned: 3,
      mrr: 9540,
      avgCreditsUsed: 142
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading reports...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-reports">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reports & Analytics</h1>
          <p className="text-gray-600">Track your practice performance and growth</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="transactions" className="flex items-center gap-2">
            <Receipt className="h-4 w-4" />
            Transactions ({transactions.length})
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Period Filter & Export */}
          <div className="flex items-center justify-end gap-2">
            <div className="flex bg-gray-100 rounded-lg p-1">
              {['week', 'month', 'quarter', 'year'].map((period) => (
                <button
                  key={period}
                  onClick={() => setReportPeriod(period)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    reportPeriod === period
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {period.charAt(0).toUpperCase() + period.slice(1)}
                </button>
              ))}
            </div>
            <Button variant="outline" onClick={exportToCSV} data-testid="export-shop-csv">
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
          </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Revenue</p>
                <p className="text-2xl font-bold">${reportData.revenue.total.toLocaleString()}</p>
                <div className="flex items-center gap-1 mt-1">
                  <TrendingUp className="h-4 w-4 text-green-500" />
                  <span className="text-sm text-green-600">+{reportData.revenue.change}%</span>
                </div>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Appointments</p>
                <p className="text-2xl font-bold">{reportData.appointments.total}</p>
                <div className="flex items-center gap-1 mt-1">
                  <TrendingUp className="h-4 w-4 text-green-500" />
                  <span className="text-sm text-green-600">+{reportData.appointments.change}%</span>
                </div>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <Calendar className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">New Patients</p>
                <p className="text-2xl font-bold">{reportData.patients.newThisMonth}</p>
                <p className="text-sm text-gray-500 mt-1">
                  {reportData.patients.memberConversion}% converted
                </p>
              </div>
              <div className="p-3 bg-purple-100 rounded-lg">
                <Users className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Monthly MRR</p>
                <p className="text-2xl font-bold">${reportData.membershipStats.mrr.toLocaleString()}</p>
                <p className="text-sm text-gray-500 mt-1">
                  {reportData.membershipStats.totalMembers} members
                </p>
              </div>
              <div className="p-3 bg-primary/20 rounded-lg">
                <CreditCard className="h-6 w-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Revenue Breakdown & Top Treatments */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Revenue Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChart className="h-5 w-5" />
              Revenue Breakdown
            </CardTitle>
            <CardDescription>Revenue by category</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {reportData.revenue.breakdown.map((item, index) => (
                <div key={index}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium">{item.category}</span>
                    <span className="text-sm text-gray-600">
                      ${item.amount.toLocaleString()} ({item.percentage}%)
                    </span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-primary rounded-full transition-all"
                      style={{ width: `${item.percentage}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Top Treatments */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Top Treatments
            </CardTitle>
            <CardDescription>Best performing services</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {reportData.topTreatments.map((treatment, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-bold text-gray-400">
                      #{index + 1}
                    </span>
                    <div>
                      <p className="font-medium">{treatment.name}</p>
                      <p className="text-sm text-gray-500">{treatment.count} sessions</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold">${treatment.revenue.toLocaleString()}</p>
                    <div className="flex items-center gap-1 justify-end">
                      {treatment.change >= 0 ? (
                        <TrendingUp className="h-3 w-3 text-green-500" />
                      ) : (
                        <TrendingDown className="h-3 w-3 text-red-500" />
                      )}
                      <span className={`text-xs ${treatment.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {treatment.change >= 0 ? '+' : ''}{treatment.change}%
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Provider Performance & Appointment Stats */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Provider Performance */}
        <Card>
          <CardHeader>
            <CardTitle>Provider Performance</CardTitle>
            <CardDescription>Revenue and ratings by provider</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {reportData.topProviders.map((provider, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center">
                      <span className="text-sm font-semibold text-primary">
                        {provider.name.split(' ').map(n => n[0]).join('')}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium">{provider.name}</p>
                      <p className="text-sm text-gray-500">{provider.appointments} appointments</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold">${provider.revenue.toLocaleString()}</p>
                    <Badge variant="secondary" className="text-xs">
                      ⭐ {provider.rating}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Appointment Stats */}
        <Card>
          <CardHeader>
            <CardTitle>Appointment Analytics</CardTitle>
            <CardDescription>Completion and cancellation rates</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Completed</span>
                  <span className="text-sm text-gray-600">
                    {reportData.appointments.completed} ({Math.round(reportData.appointments.completed / reportData.appointments.total * 100)}%)
                  </span>
                </div>
                <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-green-500 rounded-full"
                    style={{ width: `${reportData.appointments.completed / reportData.appointments.total * 100}%` }}
                  />
                </div>
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Cancelled</span>
                  <span className="text-sm text-gray-600">
                    {reportData.appointments.cancelled} ({Math.round(reportData.appointments.cancelled / reportData.appointments.total * 100)}%)
                  </span>
                </div>
                <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-yellow-500 rounded-full"
                    style={{ width: `${reportData.appointments.cancelled / reportData.appointments.total * 100}%` }}
                  />
                </div>
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">No-Show</span>
                  <span className="text-sm text-gray-600">
                    {reportData.appointments.noShow} ({Math.round(reportData.appointments.noShow / reportData.appointments.total * 100)}%)
                  </span>
                </div>
                <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-red-500 rounded-full"
                    style={{ width: `${reportData.appointments.noShow / reportData.appointments.total * 100}%` }}
                  />
                </div>
              </div>

              <div className="pt-4 border-t">
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-green-600">
                      {Math.round(reportData.appointments.completed / reportData.appointments.total * 100)}%
                    </p>
                    <p className="text-sm text-gray-600">Completion Rate</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-red-600">
                      {Math.round(reportData.appointments.noShow / reportData.appointments.total * 100)}%
                    </p>
                    <p className="text-sm text-gray-600">No-Show Rate</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Membership Insights */}
      <Card>
        <CardHeader>
          <CardTitle>Membership Insights</CardTitle>
          <CardDescription>Member growth and engagement metrics</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-primary">{reportData.membershipStats.totalMembers}</p>
              <p className="text-sm text-gray-600">Total Members</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-2xl font-bold text-green-600">+{reportData.membershipStats.newSignups}</p>
              <p className="text-sm text-gray-600">New Signups</p>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <p className="text-2xl font-bold text-red-600">-{reportData.membershipStats.churned}</p>
              <p className="text-sm text-gray-600">Churned</p>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="text-2xl font-bold text-blue-600">${reportData.membershipStats.mrr}</p>
              <p className="text-sm text-gray-600">Monthly MRR</p>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <p className="text-2xl font-bold text-purple-600">{reportData.membershipStats.avgCreditsUsed}</p>
              <p className="text-sm text-gray-600">Avg Credits/Month</p>
            </div>
          </div>
        </CardContent>
      </Card>
        </TabsContent>

        {/* Transactions Tab */}
        <TabsContent value="transactions" className="space-y-6">
          {/* Transactions Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search transactions..."
                  value={txSearchTerm}
                  onChange={(e) => { setTxSearchTerm(e.target.value); setTxCurrentPage(1); }}
                  className="pl-10 w-64"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="text-lg px-3 py-1">
                Total: ${txTotalRevenue.toLocaleString()}
              </Badge>
              <Button variant="outline" onClick={exportTransactionsCSV}>
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </div>

          {/* Transactions Table */}
          <Card>
            <CardContent className="p-0">
              {transactionsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Date</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Client</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Items</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Amount</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Discount</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Payment</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Type</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {paginatedTransactions.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                            {txSearchTerm ? 'No transactions found' : 'No transactions imported yet. Go to Data Import to import your transaction history.'}
                          </td>
                        </tr>
                      ) : (
                        paginatedTransactions.map((tx, idx) => (
                          <tr key={tx.id || idx} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm">
                              {tx.date ? new Date(tx.date).toLocaleDateString() : '-'}
                            </td>
                            <td className="px-4 py-3">
                              <div>
                                <p className="text-sm font-medium">{tx.clientName || '-'}</p>
                                <p className="text-xs text-gray-500">{tx.clientEmail || tx.phone || ''}</p>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm max-w-xs truncate">{tx.items || '-'}</td>
                            <td className="px-4 py-3 text-sm font-medium text-green-600">
                              ${(tx.amount || 0).toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-sm text-red-500">
                              {tx.discount ? `-$${tx.discount}` : '-'}
                            </td>
                            <td className="px-4 py-3 text-sm">{tx.paymentMethod || '-'}</td>
                            <td className="px-4 py-3">
                              <Badge variant="outline">{tx.type || '-'}</Badge>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Pagination */}
          {txTotalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Showing {(txCurrentPage - 1) * txItemsPerPage + 1} to {Math.min(txCurrentPage * txItemsPerPage, filteredTransactions.length)} of {filteredTransactions.length}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setTxCurrentPage(p => p - 1)}
                  disabled={txCurrentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="px-3 py-1 text-sm">Page {txCurrentPage} of {txTotalPages}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setTxCurrentPage(p => p + 1)}
                  disabled={txCurrentPage === txTotalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminReports;
